import { elements, FASTElement, html, Observable, observable, repeat, slotted } from "@microsoft/fast-element";
import { NatGenDesignSystem } from "../../natgen-design-system";
import { NatGenSelect } from "../select/select";
import { NatGenCombobox } from "../combobox/combobox";
import { listboxWizardStyles } from "./listbox-wizard.styles";
import { NatGenListboxPill } from "./listbox-pill";

export class NatGenListboxWizard extends FASTElement {
    public activeList: NatGenCombobox | NatGenSelect | undefined;
    private readonly boundOnListValueChanged = this.onListValueChanged.bind(this);

    private get filledLists(): (NatGenCombobox | NatGenSelect)[] {
        return this.slottedLists?.filter(list => !!list.currentValue) ?? [];
    }

    public get values() {
        Observable.track(this, 'values');
        return this.filledLists.map(list => list.currentValue);
    }

    public get displayValues() {
        Observable.track(this, 'displayValues');
        return this.filledLists.map(list => this.getDisplayValue(list));
    }

    public get selections() {
        Observable.track(this, 'selections');
        return this.filledLists.map(list => ({
            value: list.currentValue,
            text: this.getDisplayValue(list),
        }));
    }

    @observable
    public slottedLists: (NatGenCombobox | NatGenSelect)[];
    public slottedListsChanged(
        oldValue: (NatGenCombobox | NatGenSelect)[],
        newValue: (NatGenCombobox | NatGenSelect)[]
    ): void {
        if (this.$fastController.isConnected) {
            this.onListStateChanged(oldValue, newValue);
        }
    }

    protected onListStateChanged(
        oldValue: (NatGenCombobox | NatGenSelect)[],
        newValue: (NatGenCombobox | NatGenSelect)[]
    ): void {
        oldValue?.forEach((list: NatGenCombobox | NatGenSelect) => {
            list?.removeEventListener("change", this.boundOnListValueChanged);
        });

        newValue?.forEach((list: NatGenCombobox | NatGenSelect) => {
            list.addEventListener("change", this.boundOnListValueChanged);
        });

        this.syncActiveList(false);
    }

    protected onListValueChanged(e : Event): void {
        const element = e.target as NatGenCombobox | NatGenSelect;

        if (element.value) {
            const nextList = element.nextElementSibling as NatGenCombobox | NatGenSelect | null;
            
            Observable.notify(this, 'values');
            Observable.notify(this, 'displayValues');
            Observable.notify(this, 'selections');

            if (nextList && !nextList.value) {
                this.setActiveList(nextList);
                return;
            }
        }

        this.syncActiveList(false);
    }

    public onPillClicked(e: Event) : void {
        const element = e.target as NatGenListboxPill;
        const index = Array.from(element.parentNode.children).indexOf(element);
        
        for (let i = index; i < this.slottedLists.length; i++)
            this.slottedLists[i].currentValue = "";

        Observable.notify(this, 'values');
        Observable.notify(this, 'displayValues');
        Observable.notify(this, 'selections');

        this.setActiveList(this.slottedLists[index]);
    }

    protected setActiveList(list: NatGenCombobox | NatGenSelect | undefined, takeFocus: boolean = true) {
        this.slottedLists?.forEach(slottedList => {
            slottedList.classList.remove('active');
            slottedList.open = false;
        });

        this.activeList = list;

        if (list) {
            list.classList.add('active');
            list.open = true;
        }

        if (takeFocus)
            list?.focus();
    }

    private getDisplayValue(list: NatGenCombobox | NatGenSelect): string {
        return list.displayValue;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.onListStateChanged([], this.slottedLists ?? []);
    }

    private syncActiveList(takeFocus: boolean): void {
        const firstUnfilled = this.slottedLists?.find(list => !list.value);
        this.setActiveList(firstUnfilled, takeFocus);
    }
}

export const natgenListboxWizardDefinition = NatGenListboxWizard.compose({
    name: `${NatGenDesignSystem.prefix}-listbox-wizard`,
    template: html<NatGenListboxWizard>`
            <natgen-stack direction="row">
                ${repeat(x => x.displayValues, html`
                    <natgen-listbox-pill @click="${(x, c) => c.parent.onPillClicked(c.event)}">${x => x}</natgen-listbox-pill>
                `)}
            </natgen-stack>
            <slot ${slotted({ property: "slottedLists", filter: elements() })}></slot>
    `,
    styles: listboxWizardStyles
});
